import React, { useState, useEffect } from "react";
import { api } from "../../services/api";
import { toast } from "react-hot-toast";
import ContentManager from "../../components/admin/ContentManager";
import {
    FaBox, FaFileInvoiceDollar, FaClock, FaUsers,
    FaPlus, FaEdit, FaTrash, FaWhatsapp, FaEnvelope, FaSearch,
    FaHistory, FaTimes, FaShieldAlt, FaRedo,
    FaBuilding, FaLayerGroup, FaFilm, FaImage, FaSlidersH,
    FaUpload, FaVideo, FaExclamationTriangle
} from "react-icons/fa";

const STATUS_OPTIONS = [
    "Enquiry Received",
    "Under Review",
    "More Information Required",
    "Quotation Sent",
    "Accepted by Customer",
    "Order Confirmed",
    "Processing",
    "Dispatched",
    "Delivered",
    "Completed",
    "Rejected",
    "Cancelled"
];

const getStatusBadge = (status) => {
    switch (status) {
        case "Enquiry Received":
            return "bg-blue-50 text-blue-700 border-blue-200";
        case "Under Review":
            return "bg-amber-50 text-amber-700 border-amber-200";
        case "More Information Required":
            return "bg-purple-50 text-purple-700 border-purple-200";
        case "Quotation Sent":
            return "bg-cyan-50 text-cyan-700 border-cyan-200";
        case "Accepted by Customer":
        case "Order Confirmed":
            return "bg-emerald-50 text-emerald-700 border-emerald-200";
        case "Processing":
            return "bg-indigo-50 text-indigo-700 border-indigo-200";
        case "Dispatched":
            return "bg-teal-50 text-teal-700 border-teal-200";
        case "Delivered":
        case "Completed":
            return "bg-green-50 text-green-700 border-green-200";
        case "Rejected":
        case "Cancelled":
            return "bg-rose-50 text-rose-700 border-rose-200";
        default:
            return "bg-gray-100 text-gray-700 border-gray-200";
    }
};

const AdminDashboard = () => {
    const [activeTab, setActiveTab] = useState("overview"); // overview, enquiries, products, customers
    const [loading, setLoading] = useState(true);

    // Overview Stats
    const [stats, setStats] = useState({
        totalProducts: 0,
        totalEnquiries: 0,
        pendingEnquiries: 0,
        quotedEnquiries: 0,
        confirmedOrders: 0,
        completedOrders: 0,
        recentEnquiries: []
    });

    // Enquiries State
    const [enquiries, setEnquiries] = useState([]);
    const [selectedEnquiry, setSelectedEnquiry] = useState(null);
    const [statusFilter, setStatusFilter] = useState("All");
    const [searchEnquiryQuery, setSearchEnquiryQuery] = useState("");

    // Enquiry Edit State inside modal
    const [newStatus, setNewStatus] = useState("");
    const [quotedPriceInput, setQuotedPriceInput] = useState("");
    const [customerNotesInput, setCustomerNotesInput] = useState("");
    const [internalNotesInput, setInternalNotesInput] = useState("");
    const [updatingEnquiry, setUpdatingEnquiry] = useState(false);

    // Products State
    const [products, setProducts] = useState([]);
    const [productModalOpen, setProductModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [uploadingImages, setUploadingImages] = useState(false);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [deletingProduct, setDeletingProduct] = useState(false);
    const [showManualMediaUrl, setShowManualMediaUrl] = useState(false);
    const [productFormData, setProductFormData] = useState({
        name: "",
        modelNumber: "",
        sku: "",
        category: "Outdoor High-Power",
        price: "",
        discountPrice: "",
        description: "",
        image: "",
        images: "",
        videoUrl: "",
        isActive: true,
        isFeatured: false,
        specifications: {
            ratedPower: "150W",
            protectionRating: "IP65",
            projectionDistance: "3–25m",
            voltage: "90–220V, 50/60Hz",
            lightSourceBrand: "OSRAM",
            luminousFlux: "15,000 lm",
            lightSourceLifetime: "80,000 hours",
            colourTemperature: "9500K",
            productWeight: "3.5 kg",
            dimensions: "34 × 23 × 20 cm",
            customGoboOptions: "Single static / 4-logo rotation",
            verifiedByAdmin: true
        }
    });

    // Customers State
    const [customers, setCustomers] = useState([]);

    useEffect(() => {
        fetchAllData();
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [statsRes, enquiriesRes, productsRes, customersRes] = await Promise.allSettled([
                api.get("/api/admin/stats"),
                api.get("/api/enquiries"),
                api.get("/api/admin/products"),
                api.get("/api/admin/users")
            ]);

            if (statsRes.status === "fulfilled") {
                setStats(statsRes.value.data?.data || statsRes.value.data || {});
            }
            if (enquiriesRes.status === "fulfilled") {
                const enqData = enquiriesRes.value.data?.data || enquiriesRes.value.data || [];
                setEnquiries(Array.isArray(enqData) ? enqData : []);
            }
            if (productsRes.status === "fulfilled") {
                const prodData = productsRes.value.data?.products || productsRes.value.data || [];
                setProducts(Array.isArray(prodData) ? prodData : []);
            }
            if (customersRes.status === "fulfilled") {
                const custData = customersRes.value.data?.users || customersRes.value.data || [];
                setCustomers(Array.isArray(custData) ? custData : []);
            }
        } catch (error) {
            console.error("Failed to load admin dashboard data:", error);
            toast.error("Error loading administrative records");
        } finally {
            setLoading(false);
        }
    };

    // Filter Enquiries
    const filteredEnquiries = enquiries.filter(item => {
        const matchesStatus = statusFilter === "All" || item.status === statusFilter;
        const matchesSearch = !searchEnquiryQuery ||
            item.referenceNumber?.toLowerCase().includes(searchEnquiryQuery.toLowerCase()) ||
            item.customerName?.toLowerCase().includes(searchEnquiryQuery.toLowerCase()) ||
            item.email?.toLowerCase().includes(searchEnquiryQuery.toLowerCase()) ||
            item.productName?.toLowerCase().includes(searchEnquiryQuery.toLowerCase()) ||
            item.modelNumber?.toLowerCase().includes(searchEnquiryQuery.toLowerCase());
        return matchesStatus && matchesSearch;
    });

    // Enquiry Modal Handlers
    const handleOpenEnquiry = (enquiry) => {
        setSelectedEnquiry(enquiry);
        setNewStatus(enquiry.status || "Enquiry Received");
        setQuotedPriceInput(enquiry.quotedPrice !== undefined ? enquiry.quotedPrice : "");
        setCustomerNotesInput(enquiry.customerNotes || "");
        setInternalNotesInput(enquiry.internalNotes || "");
    };

    const handleUpdateEnquiry = async (e) => {
        e.preventDefault();
        if (!selectedEnquiry) return;
        setUpdatingEnquiry(true);
        try {
            const payload = {
                status: newStatus,
                quotedPrice: quotedPriceInput !== "" ? Number(quotedPriceInput) : undefined,
                customerNotes: customerNotesInput,
                internalNotes: internalNotesInput,
                note: customerNotesInput || `Status updated to ${newStatus}`
            };

            const { data } = await api.put(`/api/enquiries/${selectedEnquiry._id}/status`, payload);
            toast.success("Enquiry workflow updated!");
            setSelectedEnquiry(data.data || data);
            fetchAllData();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Failed to update enquiry status");
        } finally {
            setUpdatingEnquiry(false);
        }
    };

    const handleResendEmail = async (enquiryId) => {
        try {
            await api.post(`/api/enquiries/${enquiryId}/resend-email`);
            toast.success("Email notification re-triggered!");
            fetchAllData();
        } catch (err) {
            toast.error("Failed to resend email: " + (err.response?.data?.message || err.message));
        }
    };

    // Open WhatsApp Chat with Customer
    const handleWhatsAppCustomer = (enquiry) => {
        const phone = enquiry.phone?.replace(/[^0-9]/g, "");
        const formattedPhone = phone?.startsWith("91") ? phone : `91${phone}`;
        const ref = enquiry.enquiryRef || enquiry.referenceNumber || "LUMIGO";
        const msg = `Hello ${enquiry.customerName},\n\nThis is LUMIGO Commercial Sales Desk regarding your Enquiry Ref: ${ref} for ${enquiry.productName} (${enquiry.modelNumber || "Gobo Projector"}).\n\nCurrent Status: ${enquiry.status}${enquiry.quotedPrice ? `\nQuoted Price: ₹${enquiry.quotedPrice.toLocaleString()}` : ""}\n\nPlease let us know if you require technical drawings, sizing guidance, or quotation updates.`;
        window.open(`https://wa.me/${formattedPhone}?text=${encodeURIComponent(msg)}`, "_blank");
    };

    // Product Modal Handlers
    const handleOpenAddProduct = () => {
        setEditingProduct(null);
        setProductFormData({
            name: "",
            modelNumber: "",
            sku: "",
            category: "Outdoor Gobo Projectors",
            price: "",
            discountPrice: "",
            description: "",
            image: "/assets/lumigo/projector-1.jpg",
            images: "/assets/lumigo/projector-1.jpg",
            videoUrl: "",
            isActive: true,
            isFeatured: false,
            specifications: {
                ratedPower: "150W",
                protectionRating: "IP65",
                projectionDistance: "3–25m",
                voltage: "90–220V, 50/60Hz",
                lightSourceBrand: "OSRAM",
                luminousFlux: "15,000 lm",
                lightSourceLifetime: "80,000 hours",
                colourTemperature: "9500K",
                productWeight: "3.5 kg",
                dimensions: "34 × 23 × 20 cm",
                customGoboOptions: "Single static / 4-logo rotation",
                verifiedByAdmin: true
            }
        });
        setProductModalOpen(true);
    };

    const handleOpenEditProduct = (prod) => {
        setEditingProduct(prod);
        const specs = prod.specifications || {};
        const imgList = prod.images && prod.images.length > 0
            ? prod.images
            : [prod.image || "/assets/lumigo/projector-1.jpg"];

        setProductFormData({
            name: prod.name || "",
            modelNumber: prod.modelNumber || prod.sku || "",
            sku: prod.sku || "",
            category: prod.category || "Outdoor Gobo Projectors",
            price: prod.price || "",
            discountPrice: prod.discountPrice || prod.pricing?.finalPrice || "",
            description: prod.description || prod.shortDescription || "",
            image: imgList[0],
            images: imgList.join(", "),
            videoUrl: prod.video || prod.videoUrl || "",
            isActive: prod.isActive !== undefined ? prod.isActive : true,
            isFeatured: prod.isFeatured || false,
            specifications: {
                ratedPower: specs.ratedPower || specs.power || "150W",
                protectionRating: specs.protectionRating || specs.ipRating || "IP65",
                projectionDistance: specs.projectionDistance || "3–25m",
                voltage: specs.voltage || "90–220V, 50/60Hz",
                lightSourceBrand: specs.lightSourceBrand || specs.lightSource || "OSRAM",
                luminousFlux: specs.luminousFlux || "15,000 lm",
                lightSourceLifetime: specs.lightSourceLifetime || "80,000 hours",
                colourTemperature: specs.colourTemperature || specs.colorTemperature || "9500K",
                productWeight: specs.productWeight || specs.weight || "3.5 kg",
                dimensions: specs.dimensions || "34 × 23 × 20 cm",
                customGoboOptions: specs.customGoboOptions || specs.goboOptions || "Single static / 4-logo rotation",
                verifiedByAdmin: specs.verifiedByAdmin !== undefined ? specs.verifiedByAdmin : true
            }
        });
        setProductModalOpen(true);
    };

    // Upload Multi-Angle Photos from PC / Phone
    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setUploadingImages(true);
        const formData = new FormData();
        files.forEach(f => formData.append("images", f));

        try {
            const { data } = await api.post("/api/upload/images", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (data.urls && data.urls.length > 0) {
                const currentList = productFormData.images
                    ? productFormData.images.split(",").map(s => s.trim()).filter(Boolean)
                    : [];
                const updated = [...currentList, ...data.urls];
                setProductFormData(prev => ({
                    ...prev,
                    images: updated.join(", "),
                    image: updated[0] || "",
                }));
                toast.success(`Successfully uploaded ${data.urls.length} photo(s) from device!`);
            }
        } catch (err) {
            console.error("Upload error:", err);
            toast.error("Failed to upload photos: " + (err.response?.data?.message || err.message));
        } finally {
            setUploadingImages(false);
            e.target.value = "";
        }
    };

    // Upload Demonstration Video from PC / Phone
    const handleVideoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploadingVideo(true);
        const formData = new FormData();
        formData.append("video", file);

        try {
            const { data } = await api.post("/api/upload/video", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (data.url) {
                setProductFormData(prev => ({
                    ...prev,
                    videoUrl: data.url,
                }));
                toast.success("Demonstration video uploaded successfully!");
            }
        } catch (err) {
            console.error("Video upload error:", err);
            toast.error("Failed to upload video: " + (err.response?.data?.message || err.message));
        } finally {
            setUploadingVideo(false);
            e.target.value = "";
        }
    };

    // Remove photo at specific index
    const handleRemoveImage = (indexToRemove) => {
        const list = (productFormData.images || "")
            .split(",")
            .map(s => s.trim())
            .filter(Boolean);
        const updated = list.filter((_, idx) => idx !== indexToRemove);
        setProductFormData(prev => ({
            ...prev,
            images: updated.join(", "),
            image: updated[0] || "",
        }));
    };

    const handleSaveProduct = async (e) => {
        e.preventDefault();
        try {
            const imagesArray = productFormData.images
                ? productFormData.images.split(",").map(s => s.trim()).filter(Boolean)
                : [productFormData.image || "/assets/lumigo/projector-1.jpg"];

            const payload = {
                name: productFormData.name,
                modelNumber: productFormData.modelNumber,
                sku: productFormData.sku || productFormData.modelNumber,
                category: productFormData.category,
                price: Number(productFormData.price) || 0,
                discountPrice: productFormData.discountPrice ? Number(productFormData.discountPrice) : undefined,
                description: productFormData.description,
                image: imagesArray[0] || "/assets/lumigo/projector-1.jpg",
                images: imagesArray.length > 0 ? imagesArray : ["/assets/lumigo/projector-1.jpg"],
                video: productFormData.videoUrl || "",
                videoUrl: productFormData.videoUrl || "",
                isActive: productFormData.isActive,
                isFeatured: productFormData.isFeatured,
                specifications: {
                    power: productFormData.specifications.ratedPower || "150W",
                    ratedPower: productFormData.specifications.ratedPower || "150W",
                    ipRating: productFormData.specifications.protectionRating || "IP65",
                    protectionRating: productFormData.specifications.protectionRating || "IP65",
                    projectionDistance: productFormData.specifications.projectionDistance || "3–25m",
                    voltage: productFormData.specifications.voltage || "90–220V, 50/60Hz",
                    lightSource: productFormData.specifications.lightSourceBrand || "OSRAM",
                    lightSourceBrand: productFormData.specifications.lightSourceBrand || "OSRAM",
                    luminousFlux: productFormData.specifications.luminousFlux || "15,000 lm",
                    lightSourceLifetime: productFormData.specifications.lightSourceLifetime || "80,000 hours",
                    colorTemperature: productFormData.specifications.colourTemperature || "9500K",
                    colourTemperature: productFormData.specifications.colourTemperature || "9500K",
                    weight: productFormData.specifications.productWeight || "3.5 kg",
                    productWeight: productFormData.specifications.productWeight || "3.5 kg",
                    dimensions: productFormData.specifications.dimensions || "34 × 23 × 20 cm",
                    goboOptions: productFormData.specifications.customGoboOptions || "Single static / 4-logo rotation",
                    customGoboOptions: productFormData.specifications.customGoboOptions || "Single static / 4-logo rotation",
                    verifiedByAdmin: productFormData.specifications.verifiedByAdmin,
                }
            };

            if (editingProduct) {
                await api.put(`/api/admin/products/${editingProduct._id}`, payload);
                toast.success("Product updated successfully!");
            } else {
                await api.post("/api/admin/products", payload);
                toast.success("New projector model created!");
            }
            setProductModalOpen(false);
            fetchAllData();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Failed to save product");
        }
    };

    // Permanent Product Deletion with Confirmation
    const handleConfirmDeleteProduct = async () => {
        if (!productToDelete) return;
        setDeletingProduct(true);
        try {
            await api.delete(`/api/admin/products/${productToDelete._id}`);
            toast.success(`Model "${productToDelete.name}" deleted permanently.`);
            setProductToDelete(null);
            setProductModalOpen(false);
            fetchAllData();
        } catch (err) {
            console.error("Delete error:", err);
            toast.error(err.response?.data?.message || "Failed to delete product");
        } finally {
            setDeletingProduct(false);
        }
    };

    return (
        <div className="space-y-8 text-[#263746] font-sans">
            {/* Top Bar Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-6 border-b border-[#E5E7EB]">
                <div>
                    <span className="text-[#E3B629] text-xs font-black uppercase tracking-[0.25em] block">
                        Commercial Command Center
                    </span>
                    <h1 className="text-2xl sm:text-3xl font-extrabold text-[#263746] mt-1">
                        LUMIGO Admin Dashboard
                    </h1>
                    <p className="text-[#687482] text-xs mt-0.5">
                        Logged in as skhandagle1233@gmail.com • B2B Catalogue & Website Management
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={fetchAllData}
                        className="p-2.5 rounded-lg bg-[#FFFFFF] hover:bg-[#F7F5EF] text-[#263746] border border-[#E5E7EB] text-xs font-bold flex items-center gap-2 transition shadow-sm"
                        title="Refresh Data"
                    >
                        <FaRedo className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                    <button
                        onClick={handleOpenAddProduct}
                        className="bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-extrabold px-4 py-2.5 rounded-lg text-xs uppercase tracking-wider flex items-center gap-2 transition shadow-sm"
                    >
                        <FaPlus /> Add Projector Model
                    </button>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="flex gap-2 border-b border-[#E5E7EB] pb-2 overflow-x-auto">
                {[
                    { id: "overview", label: "Overview Metrics", icon: FaBox },
                    { id: "enquiries", label: `Enquiries (${enquiries.length})`, icon: FaFileInvoiceDollar },
                    { id: "products", label: `Catalogue Models (${products.length})`, icon: FaShieldAlt },
                    { id: "branding", label: "Branding & Details", icon: FaBuilding },
                    { id: "slides", label: "Hero Slides", icon: FaLayerGroup },
                    { id: "videos", label: "Video Demonstrations", icon: FaFilm },
                    { id: "gallery", label: "Photo Gallery", icon: FaImage },
                    { id: "content", label: "Sections & Copy", icon: FaSlidersH },
                    { id: "customers", label: `Customers (${customers.length})`, icon: FaUsers }
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-3.5 py-2 rounded-lg text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition whitespace-nowrap ${activeTab === tab.id ? "bg-[#E3B629] text-[#263746] shadow-sm" : "text-[#687482] hover:text-[#263746] hover:bg-[#FFFFFF]"}`}
                    >
                        <tab.icon /> {tab.label}
                    </button>
                ))}
            </div>

            {/* TAB 1: OVERVIEW METRICS */}
            {activeTab === "overview" && (
                <div className="space-y-8 animate-fadeIn">
                    {/* KPI Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        <div className="p-5 rounded-xl bg-[#FFFFFF] border border-[#E5E7EB] shadow-sm space-y-1">
                            <span className="text-[10px] text-[#687482] font-bold uppercase tracking-wider block">Total Products</span>
                            <div className="text-2xl font-black text-[#263746]">{stats.totalProducts || products.length}</div>
                            <span className="text-[10px] text-emerald-600 font-bold">10 Core Models</span>
                        </div>

                        <div className="p-5 rounded-xl bg-[#FFFFFF] border border-[#E5E7EB] shadow-sm space-y-1">
                            <span className="text-[10px] text-[#687482] font-bold uppercase tracking-wider block">Total Enquiries</span>
                            <div className="text-2xl font-black text-[#263746]">{stats.totalEnquiries || enquiries.length}</div>
                            <span className="text-[10px] text-[#687482]">Lifetime submissions</span>
                        </div>

                        <div className="p-5 rounded-xl bg-[#FFFFFF] border border-amber-200 shadow-sm space-y-1">
                            <span className="text-[10px] text-amber-700 font-bold uppercase tracking-wider block">Awaiting Action</span>
                            <div className="text-2xl font-black text-amber-600">{stats.pendingEnquiries || 0}</div>
                            <span className="text-[10px] text-[#687482]">Received / Review</span>
                        </div>

                        <div className="p-5 rounded-xl bg-[#FFFFFF] border border-cyan-200 shadow-sm space-y-1">
                            <span className="text-[10px] text-cyan-700 font-bold uppercase tracking-wider block">Quotations Sent</span>
                            <div className="text-2xl font-black text-cyan-600">{stats.quotedEnquiries || 0}</div>
                            <span className="text-[10px] text-[#687482]">Pending acceptance</span>
                        </div>

                        <div className="p-5 rounded-xl bg-[#FFFFFF] border border-emerald-200 shadow-sm space-y-1">
                            <span className="text-[10px] text-emerald-700 font-bold uppercase tracking-wider block">Confirmed Orders</span>
                            <div className="text-2xl font-black text-[#168B53]">{stats.confirmedOrders || 0}</div>
                            <span className="text-[10px] text-[#687482]">In production</span>
                        </div>

                        <div className="p-5 rounded-xl bg-[#FFFFFF] border border-[#E5E7EB] shadow-sm space-y-1">
                            <span className="text-[10px] text-[#687482] font-bold uppercase tracking-wider block">Completed</span>
                            <div className="text-2xl font-black text-[#263746]">{stats.completedOrders || 0}</div>
                            <span className="text-[10px] text-green-600 font-bold">Delivered</span>
                        </div>
                    </div>

                    {/* Quick Access to Recent Enquiries */}
                    <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-bold text-[#263746] flex items-center gap-2">
                                <FaClock className="text-[#E3B629]" /> Recently Updated Enquiries
                            </h3>
                            <button
                                onClick={() => setActiveTab("enquiries")}
                                className="text-xs text-[#E3B629] font-bold hover:underline"
                            >
                                View All &rarr;
                            </button>
                        </div>

                        {enquiries.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs">
                                    <thead className="border-b border-[#E5E7EB] bg-[#F7F5EF] text-[#687482] uppercase text-[10px]">
                                        <tr>
                                            <th className="py-2.5 px-3">Reference</th>
                                            <th className="py-2.5 px-3">Customer</th>
                                            <th className="py-2.5 px-3">Product & Model</th>
                                            <th className="py-2.5 px-3">Status</th>
                                            <th className="py-2.5 px-3">Date</th>
                                            <th className="py-2.5 px-3 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[#E5E7EB]">
                                        {enquiries.slice(0, 5).map(item => (
                                            <tr key={item._id} className="hover:bg-[#F7F5EF] transition">
                                                <td className="py-3 px-3 font-mono font-bold text-[#263746]">
                                                    {item.enquiryRef || item.referenceNumber || item._id.slice(-6)}
                                                </td>
                                                <td className="py-3 px-3">
                                                    <div className="text-[#263746] font-bold">{item.customerName}</div>
                                                    <div className="text-[#687482] text-[10px]">{item.email}</div>
                                                </td>
                                                <td className="py-3 px-3">
                                                    <div className="text-[#263746]">{item.productName}</div>
                                                    <div className="text-[#687482] text-[10px]">Qty: {item.quantity || 1} • Model: {item.modelNumber || "N/A"}</div>
                                                </td>
                                                <td className="py-3 px-3">
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${getStatusBadge(item.status)}`}>
                                                        {item.status}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-3 text-[#687482] font-mono text-[10px]">
                                                    {new Date(item.createdAt).toLocaleDateString()}
                                                </td>
                                                <td className="py-3 px-3 text-right">
                                                    <button
                                                        onClick={() => { setActiveTab("enquiries"); handleOpenEnquiry(item); }}
                                                        className="text-xs text-[#263746] bg-[#F7F5EF] hover:bg-[#E3B629] px-3 py-1 rounded transition border border-[#E5E7EB] font-bold"
                                                    >
                                                        Review
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="py-8 text-center text-xs text-[#687482]">
                                No customer enquiries logged yet.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* TAB 2: ENQUIRIES & ORDERS MANAGEMENT */}
            {activeTab === "enquiries" && (
                <div className="space-y-6 animate-fadeIn">
                    {/* Filter & Search Bar */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center bg-[#FFFFFF] p-4 rounded-xl border border-[#E5E7EB] shadow-sm">
                        <div className="flex items-center gap-3 w-full sm:w-auto">
                            <div className="relative flex-1 sm:w-64">
                                <input
                                    type="text"
                                    value={searchEnquiryQuery}
                                    onChange={(e) => setSearchEnquiryQuery(e.target.value)}
                                    placeholder="Search Ref, Name, Model..."
                                    className="w-full bg-[#F7F5EF] border border-[#E5E7EB] rounded-lg px-3 py-2 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629]"
                                />
                                <FaSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-[#687482] text-xs pointer-events-none" />
                            </div>

                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="bg-[#F7F5EF] border border-[#E5E7EB] rounded-lg px-3 py-2 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629]"
                            >
                                <option value="All">All Statuses</option>
                                {STATUS_OPTIONS.map(st => (
                                    <option key={st} value={st}>{st}</option>
                                ))}
                            </select>
                        </div>

                        <div className="text-xs text-[#687482]">
                            Showing <strong className="text-[#263746]">{filteredEnquiries.length}</strong> enquiries
                        </div>
                    </div>

                    {/* Enquiries Table */}
                    <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl overflow-hidden shadow-sm">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs">
                                <thead className="border-b border-[#E5E7EB] bg-[#F7F5EF] text-[#687482] uppercase text-[10px]">
                                    <tr>
                                        <th className="py-3 px-4">Ref Number</th>
                                        <th className="py-3 px-4">Date</th>
                                        <th className="py-3 px-4">Customer</th>
                                        <th className="py-3 px-4">Contact</th>
                                        <th className="py-3 px-4">Projector / Model</th>
                                        <th className="py-3 px-4">Quoted (₹)</th>
                                        <th className="py-3 px-4">Current Status</th>
                                        <th className="py-3 px-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[#E5E7EB]">
                                    {filteredEnquiries.length > 0 ? (
                                        filteredEnquiries.map(item => (
                                            <tr key={item._id} className="hover:bg-[#F7F5EF] transition">
                                                <td className="py-3 px-4 font-mono font-bold text-[#263746]">
                                                    {item.enquiryRef || item.referenceNumber || item._id.slice(-6)}
                                                </td>
                                                <td className="py-3 px-4 text-[#687482] font-mono text-[11px]">
                                                    {new Date(item.createdAt).toLocaleDateString()}
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div className="font-bold text-[#263746]">{item.customerName}</div>
                                                    {item.companyName && (
                                                        <div className="text-[10px] text-[#687482]">{item.companyName}</div>
                                                    )}
                                                </td>
                                                <td className="py-3 px-4 text-[11px]">
                                                    <div className="text-[#263746]">{item.email}</div>
                                                    <div className="text-[#687482] font-mono">{item.phone}</div>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div className="text-[#263746] font-medium truncate max-w-xs">{item.productName}</div>
                                                    <div className="text-[10px] text-[#687482]">Qty: {item.quantity || 1} • {item.modelNumber || "Gobo"}</div>
                                                </td>
                                                <td className="py-3 px-4 font-mono font-bold">
                                                    {item.quotedPrice ? (
                                                        <span className="text-[#168B53]">₹{item.quotedPrice.toLocaleString()}</span>
                                                    ) : (
                                                        <span className="text-[#687482]">—</span>
                                                    )}
                                                </td>
                                                <td className="py-3 px-4">
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${getStatusBadge(item.status)}`}>
                                                        {item.status}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            onClick={() => handleWhatsAppCustomer(item)}
                                                            className="p-1.5 rounded bg-emerald-50 text-[#168B53] hover:bg-[#168B53] hover:text-white transition border border-emerald-200"
                                                            title="Chat on WhatsApp"
                                                        >
                                                            <FaWhatsapp className="text-xs" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleOpenEnquiry(item)}
                                                            className="px-3 py-1.5 rounded bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-extrabold text-xs uppercase tracking-wider transition shadow-sm"
                                                        >
                                                            Process
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={8} className="py-12 text-center text-[#687482]">
                                                No enquiries match your search or filter.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 3: PRODUCT CATALOGUE MANAGEMENT */}
            {activeTab === "products" && (
                <div className="space-y-6 animate-fadeIn">
                    <div className="flex justify-between items-center bg-[#FFFFFF] p-4 rounded-xl border border-[#E5E7EB] shadow-sm">
                        <div>
                            <h3 className="text-base font-bold text-[#263746]">Commercial Gobo Projector Inventory</h3>
                            <p className="text-xs text-[#687482]">Configure specifications, standard prices, discounted prices, and active catalog status.</p>
                        </div>
                        <button
                            onClick={handleOpenAddProduct}
                            className="bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-extrabold px-4 py-2.5 rounded-lg text-xs uppercase tracking-wider flex items-center gap-2 transition shadow-sm"
                        >
                            <FaPlus /> Add Model
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {products.map(prod => {
                            const specs = prod.specifications || {};
                            return (
                                <div key={prod._id} className="bg-[#FFFFFF] border border-[#E5E7EB] hover:border-[#E3B629] rounded-xl overflow-hidden flex flex-col justify-between p-4 shadow-sm transition">
                                    <div>
                                        <div className="aspect-[4/3] bg-[#F7F5EF] rounded-lg overflow-hidden flex items-center justify-center p-3 mb-3 border border-[#E5E7EB] relative">
                                            <img
                                                src={prod.images?.[0] || prod.image || "/assets/lumigo/projector-1.jpg"}
                                                alt={prod.name}
                                                className="max-h-full max-w-full object-contain"
                                                onError={(e) => { e.target.onerror = null; e.target.src = "/assets/lumigo/projector-1.jpg"; }}
                                            />
                                            <span className="absolute top-2 left-2 bg-[#E3B629] text-[#263746] text-[9px] font-black px-2 py-0.5 rounded shadow-sm">
                                                {prod.modelNumber || prod.sku}
                                            </span>
                                            <span className={`absolute top-2 right-2 text-[9px] font-bold px-2 py-0.5 rounded border ${prod.isActive ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
                                                {prod.isActive ? "Active" : "Inactive"}
                                            </span>
                                        </div>

                                        <div className="text-[10px] text-[#E3B629] uppercase font-bold tracking-wider mb-1">
                                            {prod.category}
                                        </div>
                                        <h4 className="text-sm font-bold text-[#263746] mb-2 leading-snug line-clamp-2">
                                            {prod.name}
                                        </h4>

                                        <div className="grid grid-cols-2 gap-2 text-[10px] text-[#687482] py-2 border-y border-[#E5E7EB] mb-3">
                                            <div>Power: <strong className="text-[#263746]">{specs.ratedPower || "150W"}</strong></div>
                                            <div>Throw: <strong className="text-[#263746]">{specs.projectionDistance || "3–25m"}</strong></div>
                                            <div>IP: <strong className="text-[#263746]">{specs.protectionRating || "IP65"}</strong></div>
                                            <div>Light: <strong className="text-[#263746]">{specs.lightSourceBrand || "OSRAM"}</strong></div>
                                        </div>

                                        <div className="flex items-baseline gap-2 mb-3">
                                            <span className="text-sm font-extrabold text-[#263746] font-mono">
                                                ₹{(prod.pricing?.finalPrice || prod.discountPrice || prod.price || 0).toLocaleString()}
                                            </span>
                                            {(prod.pricing?.mrp || prod.price) > (prod.pricing?.finalPrice || prod.discountPrice || prod.price) && (
                                                <span className="text-xs text-[#9CA3AF] line-through font-mono">
                                                    ₹{(prod.pricing?.mrp || prod.price).toLocaleString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 pt-2 border-t border-[#E5E7EB]">
                                        <button
                                            onClick={() => handleOpenEditProduct(prod)}
                                            className="flex-1 bg-[#F7F5EF] hover:bg-[#E3B629] text-[#263746] font-bold py-2 rounded-lg text-xs uppercase tracking-wider transition flex items-center justify-center gap-1.5 border border-[#E5E7EB]"
                                        >
                                            <FaEdit /> Edit
                                        </button>
                                        <button
                                            onClick={() => setProductToDelete(prod)}
                                            className="px-3 py-2 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-lg text-xs font-bold transition border border-rose-200 flex items-center gap-1.5"
                                            title="Delete this projector model"
                                        >
                                            <FaTrash /> Delete
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* TAB 4: CUSTOMERS DIRECTORY */}
            {activeTab === "customers" && (
                <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl p-6 shadow-sm animate-fadeIn">
                    <h3 className="text-base font-bold text-[#263746] mb-4">Registered B2B Clients & Contacts</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                            <thead className="border-b border-[#E5E7EB] bg-[#F7F5EF] text-[#687482] uppercase text-[10px]">
                                <tr>
                                    <th className="py-2.5 px-3">Name</th>
                                    <th className="py-2.5 px-3">Email</th>
                                    <th className="py-2.5 px-3">Phone</th>
                                    <th className="py-2.5 px-3">Company</th>
                                    <th className="py-2.5 px-3">City</th>
                                    <th className="py-2.5 px-3">Role</th>
                                    <th className="py-2.5 px-3">Joined Date</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#E5E7EB]">
                                {customers.length > 0 ? (
                                    customers.map(c => (
                                        <tr key={c._id} className="hover:bg-[#F7F5EF] transition">
                                            <td className="py-3 px-3 font-bold text-[#263746]">{c.name}</td>
                                            <td className="py-3 px-3 text-[#263746]">{c.email}</td>
                                            <td className="py-3 px-3 font-mono text-[#687482]">{c.phone || "—"}</td>
                                            <td className="py-3 px-3 text-[#263746]">{c.companyName || "—"}</td>
                                            <td className="py-3 px-3 text-[#687482]">{c.city || "—"}</td>
                                            <td className="py-3 px-3">
                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${c.role === "admin" ? "bg-[#E3B629] text-[#263746]" : "bg-[#F7F5EF] text-[#687482] border border-[#E5E7EB]"}`}>
                                                    {c.role}
                                                </span>
                                            </td>
                                            <td className="py-3 px-3 font-mono text-[10px] text-[#687482]">
                                                {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : "—"}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={7} className="py-8 text-center text-[#687482]">
                                            No registered customer accounts yet.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* TABS 4, 5, 6, 7, 8: CONTENT MANAGEMENT (BRANDING, SLIDES, VIDEOS, GALLERY, SECTIONS) */}
            {["branding", "slides", "videos", "gallery", "content"].includes(activeTab) && (
                <ContentManager activeSection={activeTab} onContentUpdated={fetchAllData} />
            )}

            {/* MODAL 1: PROCESS ENQUIRY & ISSUE QUOTATION */}
            {selectedEnquiry && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 shadow-2xl">
                        <div className="flex justify-between items-start pb-4 border-b border-[#E5E7EB] mb-4">
                            <div>
                                <span className="text-[10px] text-[#E3B629] uppercase font-black tracking-widest block">
                                    Enquiry Processing Panel
                                </span>
                                <h3 className="text-xl font-bold text-[#263746] flex items-center gap-2">
                                    Reference: <span className="font-mono text-[#263746] bg-[#F7F5EF] px-2 py-0.5 rounded border border-[#E5E7EB]">{selectedEnquiry.enquiryRef || selectedEnquiry.referenceNumber || selectedEnquiry._id}</span>
                                </h3>
                                <div className="text-xs text-[#687482] mt-1">
                                    Submitted on {new Date(selectedEnquiry.createdAt).toLocaleString()}
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedEnquiry(null)}
                                className="text-[#687482] hover:text-[#263746] text-lg p-1 transition"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        {/* Customer & Product Information */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-xl bg-[#F7F5EF] border border-[#E5E7EB] text-xs mb-6">
                            <div>
                                <span className="text-[10px] text-[#687482] uppercase font-bold block mb-1">Customer Details</span>
                                <div className="font-bold text-[#263746] text-sm">{selectedEnquiry.customerName}</div>
                                <div className="text-[#263746]">{selectedEnquiry.email}</div>
                                <div className="text-[#687482] font-mono">{selectedEnquiry.phone}</div>
                                {selectedEnquiry.companyName && <div className="text-[#263746]">Company: {selectedEnquiry.companyName}</div>}
                                {selectedEnquiry.city && <div className="text-[#687482]">City: {selectedEnquiry.city}</div>}
                                {selectedEnquiry.address && <div className="text-[#687482]">Address: {selectedEnquiry.address}</div>}
                                <div className="mt-2 text-[10px] text-[#687482]">
                                    Preferred Contact: <strong className="text-[#263746] uppercase">{selectedEnquiry.preferredContact || selectedEnquiry.preferredContactMethod || "WhatsApp"}</strong>
                                </div>
                            </div>

                            <div>
                                <span className="text-[10px] text-[#687482] uppercase font-bold block mb-1">Requested Hardware</span>
                                <div className="font-bold text-[#263746] text-sm">{selectedEnquiry.productName}</div>
                                <div className="text-[#687482]">Model: {selectedEnquiry.modelNumber || "N/A"}</div>
                                <div className="text-[#687482]">Quantity: {selectedEnquiry.quantity || 1} units</div>
                                <div className="mt-2 p-2 bg-[#FFFFFF] rounded border border-[#E5E7EB] text-[11px] text-[#263746]">
                                    <strong>Requirements:</strong> {selectedEnquiry.requirements || selectedEnquiry.customerRequirements || "Standard commercial quotation & delivery"}
                                </div>
                            </div>
                        </div>

                        {/* Status Update Form */}
                        <form onSubmit={handleUpdateEnquiry} className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs text-[#263746] font-bold uppercase tracking-wider block mb-1">
                                        Update Workflow Status
                                    </label>
                                    <select
                                        value={newStatus}
                                        onChange={(e) => setNewStatus(e.target.value)}
                                        className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg p-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629]"
                                    >
                                        {STATUS_OPTIONS.map(st => (
                                            <option key={st} value={st}>{st}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="text-xs text-[#263746] font-bold uppercase tracking-wider block mb-1">
                                        Commercial Quoted Price (₹)
                                    </label>
                                    <input
                                        type="number"
                                        value={quotedPriceInput}
                                        onChange={(e) => setQuotedPriceInput(e.target.value)}
                                        placeholder="e.g. 34500"
                                        className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg p-2.5 text-xs text-[#263746] font-mono focus:outline-none focus:border-[#E3B629]"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-xs text-[#263746] font-bold uppercase tracking-wider block mb-1">
                                    Customer-Visible Status Note (Shown in User Dashboard)
                                </label>
                                <textarea
                                    rows={2}
                                    value={customerNotesInput}
                                    onChange={(e) => setCustomerNotesInput(e.target.value)}
                                    placeholder="e.g. Quotation dispatched to your email. Estimated dispatch 4 business days upon RTGS confirmation."
                                    className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg p-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629]"
                                />
                            </div>

                            <div>
                                <label className="text-xs text-[#263746] font-bold uppercase tracking-wider block mb-1">
                                    Internal Admin Notes (Private)
                                </label>
                                <input
                                    type="text"
                                    value={internalNotesInput}
                                    onChange={(e) => setInternalNotesInput(e.target.value)}
                                    placeholder="e.g. Discussed with plant engineer; glass gobo vector received on WhatsApp."
                                    className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg p-2.5 text-xs text-[#263746] focus:outline-none focus:border-[#E3B629]"
                                />
                            </div>

                            {/* Status History Audit Trail */}
                            {selectedEnquiry.statusHistory && selectedEnquiry.statusHistory.length > 0 && (
                                <div className="border border-[#E5E7EB] rounded-xl p-3 bg-[#F7F5EF]">
                                    <div className="text-[10px] text-[#687482] font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                        <FaHistory className="text-[#E3B629]" /> Status Audit History
                                    </div>
                                    <div className="space-y-1.5 max-h-32 overflow-y-auto text-[11px]">
                                        {selectedEnquiry.statusHistory.map((h, i) => (
                                            <div key={i} className="flex justify-between items-center text-[#687482] border-b border-[#E5E7EB] pb-1">
                                                <div>
                                                    <span className="text-[#263746] font-bold">{h.status}</span>
                                                    {h.note && <span className="text-[#687482] ml-2 italic">"{h.note}"</span>}
                                                </div>
                                                <span className="font-mono text-[10px]">{new Date(h.changedAt).toLocaleString()}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-[#E5E7EB]">
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => handleWhatsAppCustomer(selectedEnquiry)}
                                        className="bg-[#168B53] hover:bg-[#127244] text-white font-bold px-3 py-2 rounded-lg text-xs flex items-center gap-1.5 transition shadow-sm"
                                    >
                                        <FaWhatsapp /> Chat on WhatsApp
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleResendEmail(selectedEnquiry._id)}
                                        className="bg-[#F7F5EF] hover:bg-[#E5E7EB] text-[#263746] font-bold px-3 py-2 rounded-lg text-xs flex items-center gap-1.5 border border-[#E5E7EB] transition"
                                        title="Resend notification email"
                                    >
                                        <FaEnvelope /> Resend Email
                                    </button>
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setSelectedEnquiry(null)}
                                        className="border border-[#E5E7EB] hover:bg-[#F7F5EF] text-[#263746] px-4 py-2 rounded-lg text-xs font-bold transition"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={updatingEnquiry}
                                        className="bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-extrabold px-6 py-2 rounded-lg text-xs uppercase tracking-wider transition shadow-sm"
                                    >
                                        {updatingEnquiry ? "Saving..." : "Save Status & Changes"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL 2: ADD / EDIT PRODUCT */}
            {/* MODAL: ADD / EDIT PRODUCT */}
            {productModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
                    <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-2xl max-w-3xl w-full max-h-[92vh] flex flex-col shadow-2xl overflow-hidden my-auto">
                        {/* Modal Header */}
                        <div className="flex justify-between items-start p-4 sm:p-6 border-b border-[#E5E7EB] bg-white sticky top-0 z-10">
                            <div>
                                <span className="text-[10px] text-[#E3B629] uppercase font-black tracking-widest block">
                                    Commercial Inventory
                                </span>
                                <h3 className="text-lg sm:text-xl font-bold text-[#263746]">
                                    {editingProduct ? `Edit Model: ${editingProduct.modelNumber || editingProduct.name}` : "Add New Gobo Projector Model"}
                                </h3>
                            </div>
                            <button
                                onClick={() => setProductModalOpen(false)}
                                className="text-[#687482] hover:text-[#263746] text-lg p-1.5 rounded-lg hover:bg-[#F7F5EF] transition"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <form onSubmit={handleSaveProduct} className="overflow-y-auto p-4 sm:p-6 space-y-5 text-xs flex-1 custom-scrollbar">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[#263746] font-bold uppercase tracking-wider block mb-1">
                                        Product Name *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={productFormData.name}
                                        onChange={(e) => setProductFormData({ ...productFormData, name: e.target.value })}
                                        placeholder="e.g. DXAX-150T Outdoor Gobo Projector (150W)"
                                        className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg p-2.5 text-[#263746] focus:outline-none focus:border-[#E3B629]"
                                    />
                                </div>

                                <div>
                                    <label className="text-[#263746] font-bold uppercase tracking-wider block mb-1">
                                        Model Number / SKU *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={productFormData.modelNumber}
                                        onChange={(e) => setProductFormData({ ...productFormData, modelNumber: e.target.value, sku: e.target.value })}
                                        placeholder="e.g. DXAX-150T"
                                        className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg p-2.5 text-[#263746] font-mono focus:outline-none focus:border-[#E3B629]"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div>
                                    <label className="text-[#263746] font-bold uppercase tracking-wider block mb-1">
                                        Category *
                                    </label>
                                    <select
                                        value={productFormData.category}
                                        onChange={(e) => setProductFormData({ ...productFormData, category: e.target.value })}
                                        className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg p-2.5 text-[#263746] focus:outline-none focus:border-[#E3B629]"
                                    >
                                        <option value="Outdoor Gobo Projectors">Outdoor Gobo Projectors</option>
                                        <option value="Indoor Logo Projectors">Indoor Logo Projectors</option>
                                        <option value="Dynamic & Effect Projectors">Dynamic & Effect Projectors</option>
                                        <option value="Custom Glass Gobos">Custom Glass Gobos</option>
                                        <option value="Architectural & Festive">Architectural & Festive</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="text-[#263746] font-bold uppercase tracking-wider block mb-1">
                                        Standard Price (₹) *
                                    </label>
                                    <input
                                        type="number"
                                        required
                                        value={productFormData.price}
                                        onChange={(e) => setProductFormData({ ...productFormData, price: e.target.value })}
                                        placeholder="34500"
                                        className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg p-2.5 text-[#263746] font-mono focus:outline-none focus:border-[#E3B629]"
                                    />
                                </div>

                                <div>
                                    <label className="text-[#263746] font-bold uppercase tracking-wider block mb-1">
                                        Discounted Price (₹, Optional)
                                    </label>
                                    <input
                                        type="number"
                                        value={productFormData.discountPrice}
                                        onChange={(e) => setProductFormData({ ...productFormData, discountPrice: e.target.value })}
                                        placeholder="31000"
                                        className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg p-2.5 text-[#263746] font-mono focus:outline-none focus:border-[#E3B629]"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-[#263746] font-bold uppercase tracking-wider block mb-1">
                                    Short Description
                                </label>
                                <textarea
                                    rows={2}
                                    value={productFormData.description}
                                    onChange={(e) => setProductFormData({ ...productFormData, description: e.target.value })}
                                    placeholder="Commercial high-power gobo projection light with OSRAM optical engine..."
                                    className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg p-2.5 text-[#263746] focus:outline-none focus:border-[#E3B629]"
                                />
                            </div>

                            {/* UPLOAD SECTION: Multi-Angle Photos & Demonstration Video */}
                            <div className="p-4 rounded-xl bg-[#F7F5EF] border border-[#E5E7EB] space-y-4">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                    <div>
                                        <span className="text-xs font-bold uppercase text-[#263746] block">
                                            Product Media (Photos & Video from Device)
                                        </span>
                                        <span className="text-[11px] text-[#687482]">
                                            Upload multiple angles from your PC, laptop, or phone. All files are optional.
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setShowManualMediaUrl(!showManualMediaUrl)}
                                        className="text-[11px] text-[#E3B629] font-bold hover:underline self-start sm:self-auto"
                                    >
                                        {showManualMediaUrl ? "Hide URL inputs" : "Direct URL input mode"}
                                    </button>
                                </div>

                                {/* Multi-Angle Photos Upload */}
                                <div>
                                    <label className="text-[11px] font-bold text-[#263746] uppercase tracking-wider block mb-2">
                                        Multi-Angle Product Photos
                                    </label>

                                    <div className="flex flex-wrap items-center gap-3">
                                        <label className={`cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition shadow-sm ${uploadingImages ? "bg-gray-300 text-gray-600 cursor-not-allowed" : "bg-[#E3B629] hover:bg-[#cca021] text-[#263746]"}`}>
                                            <FaUpload className={uploadingImages ? "animate-spin" : ""} />
                                            <span>{uploadingImages ? "Uploading Photos..." : "Upload Photos from Device (PC/Phone)"}</span>
                                            <input
                                                type="file"
                                                multiple
                                                accept="image/*"
                                                disabled={uploadingImages}
                                                onChange={handleImageUpload}
                                                className="hidden"
                                            />
                                        </label>
                                        <span className="text-[11px] text-[#687482]">
                                            Optional: Front, side, ceiling mount, floor projection angles
                                        </span>
                                    </div>

                                    {/* Uploaded Photos Grid Preview */}
                                    {(() => {
                                        const imgList = (productFormData.images || "")
                                            .split(",")
                                            .map(s => s.trim())
                                            .filter(Boolean);

                                        if (imgList.length === 0) return null;

                                        return (
                                            <div className="mt-3">
                                                <span className="text-[10px] text-[#687482] font-bold uppercase tracking-wider block mb-1.5">
                                                    Attached Angles ({imgList.length}):
                                                </span>
                                                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                                                    {imgList.map((imgUrl, idx) => (
                                                        <div key={idx} className="relative group bg-white border border-[#E5E7EB] rounded-lg p-1 aspect-square flex items-center justify-center overflow-hidden shadow-xs">
                                                            <img
                                                                src={imgUrl}
                                                                alt={`Angle ${idx + 1}`}
                                                                className="max-h-full max-w-full object-contain"
                                                                onError={(e) => { e.target.onerror = null; e.target.src = "/assets/lumigo/projector-1.jpg"; }}
                                                            />
                                                            <span className={`absolute top-1 left-1 text-[8px] font-black px-1.5 py-0.5 rounded shadow-xs ${idx === 0 ? "bg-[#E3B629] text-[#263746]" : "bg-black/60 text-white"}`}>
                                                                {idx === 0 ? "Cover" : `#${idx + 1}`}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveImage(idx)}
                                                                className="absolute top-1 right-1 bg-rose-600 hover:bg-rose-700 text-white rounded-full p-1 text-[8px] shadow-sm transition"
                                                                title="Remove this photo"
                                                            >
                                                                <FaTimes />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* Manual Comma-separated paths */}
                                    {showManualMediaUrl && (
                                        <div className="mt-2.5">
                                            <label className="text-[10px] text-[#687482] block mb-1">
                                                Image paths / URLs (comma-separated):
                                            </label>
                                            <input
                                                type="text"
                                                value={productFormData.images}
                                                onChange={(e) => setProductFormData({ ...productFormData, images: e.target.value, image: e.target.value.split(",")[0]?.trim() })}
                                                placeholder="/assets/lumigo/projector-1.jpg, /assets/lumigo/projector-2.jpg"
                                                className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg p-2 text-[11px] text-[#263746] font-mono focus:outline-none focus:border-[#E3B629]"
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Demonstration Video Upload */}
                                <div className="pt-3 border-t border-[#E5E7EB]">
                                    <label className="text-[11px] font-bold text-[#263746] uppercase tracking-wider block mb-2">
                                        Demonstration Video
                                    </label>

                                    <div className="flex flex-wrap items-center gap-3">
                                        <label className={`cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-bold transition shadow-sm ${uploadingVideo ? "bg-gray-300 text-gray-600 cursor-not-allowed" : "bg-[#263746] hover:bg-[#1A232E] text-white"}`}>
                                            <FaVideo className={uploadingVideo ? "animate-spin" : ""} />
                                            <span>{uploadingVideo ? "Uploading Video..." : "Upload Demo Video from Device"}</span>
                                            <input
                                                type="file"
                                                accept="video/*"
                                                disabled={uploadingVideo}
                                                onChange={handleVideoUpload}
                                                className="hidden"
                                            />
                                        </label>
                                        <span className="text-[11px] text-[#687482]">
                                            Optional: MP4 or WEBM file demonstrating projection effects
                                        </span>
                                    </div>

                                    {productFormData.videoUrl && (
                                        <div className="mt-2.5 flex items-center justify-between p-2.5 bg-white border border-[#E5E7EB] rounded-lg">
                                            <div className="flex items-center gap-2 text-xs text-[#263746] truncate">
                                                <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold">▶ Video Ready</span>
                                                <span className="font-mono text-[11px] text-[#687482] truncate">{productFormData.videoUrl}</span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setProductFormData({ ...productFormData, videoUrl: "" })}
                                                className="text-rose-600 hover:text-rose-700 text-xs font-bold px-2 py-1 rounded hover:bg-rose-50 transition ml-2"
                                            >
                                                Remove Video
                                            </button>
                                        </div>
                                    )}

                                    {showManualMediaUrl && (
                                        <div className="mt-2.5">
                                            <label className="text-[10px] text-[#687482] block mb-1">
                                                Video URL / Path:
                                            </label>
                                            <input
                                                type="text"
                                                value={productFormData.videoUrl}
                                                onChange={(e) => setProductFormData({ ...productFormData, videoUrl: e.target.value })}
                                                placeholder="/assets/lumigo/video-demo-1.mp4"
                                                className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded-lg p-2 text-[11px] text-[#263746] font-mono focus:outline-none focus:border-[#E3B629]"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Technical Specifications Sub-Form */}
                            <div className="p-4 rounded-xl bg-[#F7F5EF] border border-[#E5E7EB] space-y-3">
                                <span className="text-xs font-bold uppercase text-[#E3B629] block">
                                    Technical Specifications (Verified & Editable)
                                </span>

                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    <div>
                                        <label className="text-[#687482] block mb-0.5">Rated Power</label>
                                        <input
                                            type="text"
                                            value={productFormData.specifications.ratedPower}
                                            onChange={(e) => setProductFormData({ ...productFormData, specifications: { ...productFormData.specifications, ratedPower: e.target.value } })}
                                            className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded p-1.5 text-[#263746]"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[#687482] block mb-0.5">Protection (IP)</label>
                                        <input
                                            type="text"
                                            value={productFormData.specifications.protectionRating}
                                            onChange={(e) => setProductFormData({ ...productFormData, specifications: { ...productFormData.specifications, protectionRating: e.target.value } })}
                                            className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded p-1.5 text-[#263746]"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[#687482] block mb-0.5">Throw Distance</label>
                                        <input
                                            type="text"
                                            value={productFormData.specifications.projectionDistance}
                                            onChange={(e) => setProductFormData({ ...productFormData, specifications: { ...productFormData.specifications, projectionDistance: e.target.value } })}
                                            className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded p-1.5 text-[#263746]"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[#687482] block mb-0.5">Voltage</label>
                                        <input
                                            type="text"
                                            value={productFormData.specifications.voltage}
                                            onChange={(e) => setProductFormData({ ...productFormData, specifications: { ...productFormData.specifications, voltage: e.target.value } })}
                                            className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded p-1.5 text-[#263746]"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    <div>
                                        <label className="text-[#687482] block mb-0.5">Light Source</label>
                                        <input
                                            type="text"
                                            value={productFormData.specifications.lightSourceBrand}
                                            onChange={(e) => setProductFormData({ ...productFormData, specifications: { ...productFormData.specifications, lightSourceBrand: e.target.value } })}
                                            className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded p-1.5 text-[#263746]"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[#687482] block mb-0.5">Luminous Flux</label>
                                        <input
                                            type="text"
                                            value={productFormData.specifications.luminousFlux}
                                            onChange={(e) => setProductFormData({ ...productFormData, specifications: { ...productFormData.specifications, luminousFlux: e.target.value } })}
                                            className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded p-1.5 text-[#263746]"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[#687482] block mb-0.5">Weight</label>
                                        <input
                                            type="text"
                                            value={productFormData.specifications.productWeight}
                                            onChange={(e) => setProductFormData({ ...productFormData, specifications: { ...productFormData.specifications, productWeight: e.target.value } })}
                                            className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded p-1.5 text-[#263746]"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[#687482] block mb-0.5">Colour Temp</label>
                                        <input
                                            type="text"
                                            value={productFormData.specifications.colourTemperature}
                                            onChange={(e) => setProductFormData({ ...productFormData, specifications: { ...productFormData.specifications, colourTemperature: e.target.value } })}
                                            className="w-full bg-[#FFFFFF] border border-[#E5E7EB] rounded p-1.5 text-[#263746]"
                                        />
                                    </div>
                                </div>

                                <div className="flex flex-wrap items-center gap-4 sm:gap-6 pt-2">
                                    <label className="flex items-center gap-2 cursor-pointer text-[#263746]">
                                        <input
                                            type="checkbox"
                                            checked={productFormData.specifications.verifiedByAdmin}
                                            onChange={(e) => setProductFormData({ ...productFormData, specifications: { ...productFormData.specifications, verifiedByAdmin: e.target.checked } })}
                                            className="accent-[#E3B629] w-4 h-4"
                                        />
                                        <span>Mark Specifications as Verified</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer text-[#263746]">
                                        <input
                                            type="checkbox"
                                            checked={productFormData.isActive}
                                            onChange={(e) => setProductFormData({ ...productFormData, isActive: e.target.checked })}
                                            className="accent-[#E3B629] w-4 h-4"
                                        />
                                        <span>Active on Public Catalogue</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer text-[#263746]">
                                        <input
                                            type="checkbox"
                                            checked={productFormData.isFeatured}
                                            onChange={(e) => setProductFormData({ ...productFormData, isFeatured: e.target.checked })}
                                            className="accent-[#E3B629] w-4 h-4"
                                        />
                                        <span>Featured Model</span>
                                    </label>
                                </div>
                            </div>

                            {/* Modal Footer with Actions */}
                            <div className="flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-3 pt-4 border-t border-[#E5E7EB] bg-white sticky bottom-0 z-10">
                                {editingProduct ? (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProductModalOpen(false);
                                            setProductToDelete(editingProduct);
                                        }}
                                        className="bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 font-bold px-4 py-2.5 rounded-lg text-xs flex items-center justify-center gap-1.5 transition"
                                    >
                                        <FaTrash /> Delete This Model
                                    </button>
                                ) : <div />}

                                <div className="flex items-center gap-3 justify-end w-full sm:w-auto">
                                    <button
                                        type="button"
                                        onClick={() => setProductModalOpen(false)}
                                        className="border border-[#E5E7EB] text-[#263746] hover:bg-[#F7F5EF] px-5 py-2.5 rounded-lg text-xs font-bold transition flex-1 sm:flex-initial"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-extrabold px-6 py-2.5 rounded-lg text-xs uppercase tracking-wider transition shadow-sm flex-1 sm:flex-initial"
                                    >
                                        {editingProduct ? "Save Changes" : "Create Model"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL: DELETE PRODUCT CONFIRMATION */}
            {productToDelete && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
                    <div className="bg-white border border-rose-200 rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 animate-scaleUp">
                        <div className="w-12 h-12 rounded-full bg-rose-50 border border-rose-200 text-rose-600 flex items-center justify-center mx-auto text-xl">
                            <FaExclamationTriangle />
                        </div>

                        <div className="text-center">
                            <h3 className="text-lg font-bold text-[#263746]">
                                Delete Projector Model?
                            </h3>
                            <p className="text-xs text-[#687482] mt-1.5 leading-relaxed">
                                Are you sure you want to delete <strong className="text-[#263746]">{productToDelete.name}</strong> ({productToDelete.modelNumber})?
                            </p>
                            <p className="text-[11px] text-rose-600 bg-rose-50 p-2 rounded-lg mt-3 font-medium">
                                This will permanently remove the product and its specifications from the public website catalogue and database.
                            </p>
                        </div>

                        <div className="flex items-center justify-end gap-3 pt-3 border-t border-[#E5E7EB]">
                            <button
                                type="button"
                                disabled={deletingProduct}
                                onClick={() => setProductToDelete(null)}
                                className="flex-1 py-2.5 border border-[#E5E7EB] rounded-lg text-xs font-bold text-[#263746] hover:bg-[#F7F5EF] transition"
                            >
                                Keep Model
                            </button>
                            <button
                                type="button"
                                disabled={deletingProduct}
                                onClick={handleConfirmDeleteProduct}
                                className="flex-1 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-bold transition shadow-sm flex items-center justify-center gap-2"
                            >
                                {deletingProduct ? (
                                    <>
                                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        <span>Deleting...</span>
                                    </>
                                ) : (
                                    <>
                                        <FaTrash className="text-xs" />
                                        <span>Delete Permanently</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
